import React, { useCallback, useEffect, useRef, useState } from "react";

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

function splitSentences(text) {
  return (text.match(/[^.!?…]*(?:[.!?…]+["')]*\s*|$)/g) || [text])
    .map(s => s.trim())
    .filter(s => s.length > 3);
}

function fmtTime(secs) {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function BlogReadAloud({ title, bodyRef }) {
  const [open, setOpen]             = useState(false);
  const [playing, setPlaying]       = useState(false);
  const [voices, setVoices]         = useState([]);
  const [voiceUri, setVoiceUri]     = useState("");
  const [speed, setSpeed]           = useState(1);
  const [elapsed, setElapsed]       = useState(0);
  const [currentText, setCurrentText] = useState("");

  // Mutable refs (read inside callbacks without stale-closure issues)
  const segsRef      = useRef([]);   // [{blockId, text}]
  const segIdxRef    = useRef(0);
  const timerRef     = useRef(null);
  const startMsRef   = useRef(0);
  const speedRef     = useRef(1);
  const voiceUriRef  = useRef("");
  const playingRef   = useRef(false);
  const speakRef     = useRef(null); // self-reference to avoid stale closures

  useEffect(() => { speedRef.current   = speed;    }, [speed]);
  useEffect(() => { voiceUriRef.current = voiceUri; }, [voiceUri]);

  // ── Load browser TTS voices ──────────────────────────────────────
  useEffect(() => {
    const load = () => {
      const all = window.speechSynthesis.getVoices();
      const en  = all.filter(v => v.lang.startsWith("en"));
      if (!en.length) return;
      setVoices(en);
      if (!voiceUriRef.current) {
        const def = en.find(v => v.default) || en[0];
        setVoiceUri(def.voiceURI);
        voiceUriRef.current = def.voiceURI;
      }
    };
    load();
    window.speechSynthesis.addEventListener("voiceschanged", load);
    return () => window.speechSynthesis.removeEventListener("voiceschanged", load);
  }, []);

  // ── Parse body DOM into TTS segments ────────────────────────────
  useEffect(() => {
    if (!bodyRef?.current) return;
    const t = setTimeout(() => {
      const container = bodyRef.current;
      if (!container) return;

      const blockEls = Array.from(
        container.querySelectorAll("p, h2, h3, h4, li, blockquote")
      ).filter(el => el.textContent.trim().length > 5);

      const segs = [];
      if (title) segs.push({ blockId: -1, text: title });

      blockEls.forEach((el, i) => {
        el.dataset.ttsId = String(i);
        const sents = splitSentences(el.textContent.trim());
        (sents.length ? sents : [el.textContent.trim()]).forEach(s =>
          segs.push({ blockId: i, text: s })
        );
      });

      segsRef.current = segs;
    }, 400);
    return () => clearTimeout(t);
  }, [bodyRef, title]);

  // ── DOM highlight helpers ────────────────────────────────────────
  const clearHighlight = useCallback(() => {
    bodyRef?.current
      ?.querySelectorAll("[data-tts-id]")
      .forEach(el => el.classList.remove("tts-active"));
  }, [bodyRef]);

  const highlight = useCallback(blockId => {
    clearHighlight();
    if (blockId < 0 || !bodyRef?.current) return;
    const el = bodyRef.current.querySelector(`[data-tts-id="${blockId}"]`);
    if (el) {
      el.classList.add("tts-active");
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [bodyRef, clearHighlight]);

  // ── Core speak function ──────────────────────────────────────────
  const speak = useCallback(idx => {
    const segs = segsRef.current;

    if (idx >= segs.length || !playingRef.current) {
      // Reached end or was stopped
      window.speechSynthesis.cancel();
      setPlaying(false);
      playingRef.current = false;
      setCurrentText("");
      clearHighlight();
      clearInterval(timerRef.current);
      segIdxRef.current = 0;
      return;
    }

    const seg = segs[idx];
    setCurrentText(seg.text);
    highlight(seg.blockId);

    const utter = new SpeechSynthesisUtterance(seg.text);
    utter.rate = speedRef.current;

    const voice = window.speechSynthesis
      .getVoices()
      .find(v => v.voiceURI === voiceUriRef.current);
    if (voice) utter.voice = voice;

    utter.onend = () => {
      if (!playingRef.current) return;
      const next = idx + 1;
      segIdxRef.current = next;
      speakRef.current?.(next);
    };

    utter.onerror = e => {
      if (e.error === "interrupted") return;
      console.error("TTS error:", e.error);
      setPlaying(false);
      playingRef.current = false;
    };

    window.speechSynthesis.speak(utter);
  }, [highlight, clearHighlight]);

  // Keep speakRef in sync
  useEffect(() => { speakRef.current = speak; }, [speak]);

  // Cleanup on unmount
  useEffect(() => () => {
    window.speechSynthesis.cancel();
    clearInterval(timerRef.current);
  }, []);

  // ── Timer helpers ────────────────────────────────────────────────
  function startTimer() {
    clearInterval(timerRef.current);
    startMsRef.current = Date.now() - elapsed * 1000;
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startMsRef.current) / 1000));
    }, 500);
  }

  // ── Playback controls ────────────────────────────────────────────
  function handlePlay() {
    if (playing) return;
    window.speechSynthesis.cancel();
    setPlaying(true);
    playingRef.current = true;
    startTimer();
    speakRef.current(segIdxRef.current);
  }

  function handlePause() {
    window.speechSynthesis.cancel();
    setPlaying(false);
    playingRef.current = false;
    clearInterval(timerRef.current);
    clearHighlight();
    setCurrentText("");
    // segIdxRef keeps position for resume
  }

  function handleStop() {
    window.speechSynthesis.cancel();
    setPlaying(false);
    playingRef.current = false;
    clearInterval(timerRef.current);
    clearHighlight();
    setCurrentText("");
    setElapsed(0);
    segIdxRef.current = 0;
  }

  function handleSpeed(s) {
    setSpeed(s);
    speedRef.current = s;
    if (playing) {
      window.speechSynthesis.cancel();
      setTimeout(() => speakRef.current?.(segIdxRef.current), 50);
    }
  }

  function handleVoice(uri) {
    setVoiceUri(uri);
    voiceUriRef.current = uri;
    if (playing) {
      window.speechSynthesis.cancel();
      setTimeout(() => speakRef.current?.(segIdxRef.current), 50);
    }
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className="tts-fab-wrapper">
      {open && (
        <div className="tts-panel" role="region" aria-label="Read aloud controls">
          <div className="tts-panel-header">
            <span>
              <i className="bi bi-volume-up-fill me-2" />
              Read Aloud
            </span>
            <button
              className="tts-close"
              onClick={() => setOpen(false)}
              aria-label="Close reader"
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {voices.length > 1 && (
            <div className="tts-row">
              <label className="tts-label">Voice</label>
              <select
                className="tts-select"
                value={voiceUri}
                onChange={e => handleVoice(e.target.value)}
              >
                {voices.map(v => (
                  <option key={v.voiceURI} value={v.voiceURI}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="tts-row">
            <label className="tts-label">Speed</label>
            <div className="tts-speed-group">
              {SPEEDS.map(s => (
                <button
                  key={s}
                  className={`tts-speed-btn${speed === s ? " active" : ""}`}
                  onClick={() => handleSpeed(s)}
                >
                  {s === 1 ? "1×" : `${s}×`}
                </button>
              ))}
            </div>
          </div>

          <div className="tts-controls">
            <button
              className="tts-ctrl-btn"
              onClick={playing ? handlePause : handlePlay}
              title={playing ? "Pause" : "Play"}
              aria-label={playing ? "Pause" : "Play"}
            >
              <i className={`bi ${playing ? "bi-pause-fill" : "bi-play-fill"}`} />
            </button>
            <button
              className="tts-ctrl-btn"
              onClick={handleStop}
              title="Stop"
              aria-label="Stop"
            >
              <i className="bi bi-stop-fill" />
            </button>
            <span className="tts-elapsed">{fmtTime(elapsed)}</span>
          </div>

          {currentText && (
            <p className="tts-current-text">"{currentText}"</p>
          )}
        </div>
      )}

      <button
        className={`tts-fab${playing ? " tts-fab--playing" : ""}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Read aloud"
        aria-expanded={open}
        title={open ? "Close reader" : "Read aloud"}
      >
        <i className={`bi ${playing ? "bi-volume-up-fill" : "bi-volume-up"}`} />
      </button>
    </div>
  );
}
